import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@app/store";
import { OrderAPI } from "@app/services/api";
import { LatLng } from "@app/domain";

/**
 * Polls the driver's live position while `enabled` is true, and stops
 * (clearing the interval, not just skipping fetches) the moment it flips to
 * false — mirrors legacy `layout/map-section`'s `updateDriverPinInterval`
 * exactly, including its interval source: `state.setting.trackDriverTimer`
 * (server-provided, falls back to 5000ms via `settingFallback`), not a
 * hardcoded constant. The caller (`Map/hooks/useOrderMapMarkers.ts`) computes
 * `enabled` from the same condition the brief specifies:
 * `(status === "Started" || "Arrived" || "PickedUp") && userActive`.
 */
export function useDriverLocation(orderId: string, enabled: boolean): LatLng | null {
  const restServerUrl = useSelector((state: RootState) => state.setting.restServerUrl);
  const trackDriverTimer = useSelector((state: RootState) => state.setting.trackDriverTimer);
  const language = useSelector((state: RootState) => state.setting.language);
  const accessToken = useSelector((state: RootState) => state.customer.accessToken);
  const customerId = useSelector((state: RootState) => state.customer.customerId);

  const [location, setLocation] = useState<LatLng | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const id = ++requestId.current;

    const poll = () => {
      new OrderAPI()
        .trackDriver({ restUrl: restServerUrl, accessToken, orderId, customerId, language })
        .then((response) => {
          if (cancelled || requestId.current !== id) return;
          setLocation({ lat: response.data.location[1], lng: response.data.location[0] });
        })
        .catch(() => {
          // Matches legacy `getTrackDateAndUpdate`: swallow the error,
          // polling continues on the next tick.
        });
    };

    poll();
    const interval = setInterval(poll, trackDriverTimer);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [orderId, enabled, trackDriverTimer, restServerUrl, accessToken, customerId, language]);

  useEffect(() => {
    if (!enabled) setLocation(null);
  }, [enabled]);

  return location;
}
